import React, {
  createContext, useContext, useState, useEffect, ReactNode, useMemo,
} from 'react';
import { IDisplayStatus } from '../graphql';

interface PreviewContextType {
  isPreviewMode: boolean;
  displayStatus: IDisplayStatus;
  togglePreview: () => void;
  setPreviewMode: (enabled: boolean) => void;
}

const PreviewContext = createContext<PreviewContextType | undefined>(undefined);

export const PreviewProvider = ({ children }: { children: ReactNode }) => {
  const [isPreviewMode, setIsPreviewMode] = useState(false);

  // Load preview state from localStorage on mount
  useEffect(() => {
    const savedPreviewMode = localStorage.getItem('admin-preview-mode');
    if (savedPreviewMode === 'true') {
      setIsPreviewMode(true);
    }
  }, []);

  // Save preview state to localStorage when changed
  useEffect(() => {
    localStorage.setItem('admin-preview-mode', isPreviewMode.toString());
  }, [isPreviewMode]);

  const togglePreview = () => {
    setIsPreviewMode((prev) => !prev);
  };

  const setPreviewMode = (enabled: boolean) => {
    setIsPreviewMode(enabled);
  };

  const displayStatus = isPreviewMode ? IDisplayStatus.Preview : IDisplayStatus.Live;

  const contextValue = useMemo(() => ({
    isPreviewMode,
    displayStatus,
    togglePreview,
    setPreviewMode,
  }), [isPreviewMode, displayStatus, togglePreview, setPreviewMode]);

  return (
    <PreviewContext.Provider value={contextValue}>
      {children}
    </PreviewContext.Provider>
  );
};

export const usePreview = () => {
  const context = useContext(PreviewContext);
  if (context === undefined) {
    throw new Error('usePreview must be used within a PreviewProvider');
  }
  return context;
};
